import React, { useState, useEffect } from 'react';
import { Hash, Mail, UserRound } from 'lucide-react';
import { useTranslations } from '../translations';
import { increaseUserAnnouncementLimit, searchUsers, updateUserStatus } from '../services/admin.api';
import { AdminUser } from '../services/api.types';
import AdminPagination from '../components/AdminPagination';
import AdminUserCard from '../components/AdminUserCard';
import AdminUserDetailModal from '../components/AdminUserDetailModal';
import { getUserPublicName } from '../utils/userSeo';
import { useNotification } from '../components/NotificationProvider';
import './AdminUsers.css';

interface AdminUsersProps {
  onNavigateToUserDetails: (userId: string, publicName?: string) => void;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ onNavigateToUserDetails }) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'BLOCKED'>('ACTIVE');
  const [idFilter, setIdFilter] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [userDetailModal, setUserDetailModal] = useState<{
    show: boolean;
    user: AdminUser | null;
  }>({ show: false, user: null });

  useEffect(() => {
    loadUsers();
  }, [currentPage, statusFilter]);

  const loadUsers = async (pageToLoad = currentPage) => {
    setLoading(true);
    setError(null);

    try {
      const response = await searchUsers({
        page: pageToLoad,
        size: 10,
        status: statusFilter,
        id: idFilter.trim(),
        username: usernameFilter.trim(),
        email: emailFilter.trim(),
      });

      console.log('Users loaded:', response);
      setUsers(response.content || []);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      setError(t.administration.users.errorLoading);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (currentPage === 1) {
      loadUsers(1);
    } else {
      setCurrentPage(1);
    }
  };

  const handleUserClick = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Set status from the filter or default to ACTIVE
    const userWithStatus = { ...user, status: user.status || statusFilter };

    setUserDetailModal({
      show: true,
      user: userWithStatus,
    });
  };

  const handleBlockUnblock = async (userId: string, newStatus: 'ACTIVE' | 'BLOCKED', reason: string) => {
    try {
      await updateUserStatus(userId, newStatus, reason ? [reason] : []);
      setUserDetailModal({ show: false, user: null });
      showNotification(
        newStatus === 'BLOCKED' ? 'Utilizatorul a fost blocat cu succes' : 'Utilizatorul a fost deblocat cu succes',
        'success'
      );
      loadUsers();
    } catch (err) {
      console.error('Error updating user status:', err);
      showNotification('Nu am putut actualiza statusul utilizatorului. Vă rugăm încercați din nou.', 'error');
    }
  };

  const handleIncreaseAnnouncementLimit = async (userId: string, announcementLimit: number) => {
    try {
      await increaseUserAnnouncementLimit(userId, announcementLimit);
      setUsers((currentUsers) => currentUsers.map((user) => (
        user.id === userId ? { ...user, announcementLimit } : user
      )));
      setUserDetailModal((currentModal) => ({
        ...currentModal,
        user: currentModal.user?.id === userId
          ? { ...currentModal.user, announcementLimit }
          : currentModal.user,
      }));
      showNotification(t.administration.users.announcementLimitSuccess, 'success');
    } catch (err) {
      console.error('Error increasing user announcement limit:', err);
      showNotification(t.administration.users.announcementLimitError, 'error');
      throw err;
    }
  };

  return (
    <div className="admin-users">
      <div className="admin-users-filters">
        <div className="admin-users-search">
          <div className="admin-users-search-field">
            <Hash className="admin-users-search-icon" aria-hidden />
            <input
              type="text"
              placeholder="User ID"
              aria-label="User ID"
              value={idFilter}
              onChange={(e) => setIdFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="admin-users-search-input"
            />
          </div>
          <div className="admin-users-search-field">
            <UserRound className="admin-users-search-icon" aria-hidden />
            <input
              type="text"
              placeholder={t.administration.users.search}
              aria-label="Username"
              value={usernameFilter}
              onChange={(e) => setUsernameFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="admin-users-search-input"
            />
          </div>
          <div className="admin-users-search-field">
            <Mail className="admin-users-search-icon" aria-hidden />
            <input
              type="email"
              placeholder="email@example.com"
              aria-label="Email"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="admin-users-search-input"
            />
          </div>
          <button onClick={handleSearch} className="admin-users-search-btn">
            {t.common.search}
          </button>
        </div>
        
        <div className="admin-users-status-filters">
          <label className="admin-users-status-checkbox">
            <input
              type="checkbox"
              checked={statusFilter === 'ACTIVE'}
              onChange={() => {
                setStatusFilter('ACTIVE');
                setCurrentPage(1);
              }}
            />
            <span>Active</span>
          </label>
          <label className="admin-users-status-checkbox">
            <input
              type="checkbox"
              checked={statusFilter === 'BLOCKED'}
              onChange={() => {
                setStatusFilter('BLOCKED');
                setCurrentPage(1);
              }}
            />
            <span>Blocat</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="admin-users-loading">{t.common.loading}</div>
      ) : error ? (
        <div className="admin-users-error">{error}</div>
      ) : users.length === 0 ? (
        <div className="admin-users-empty">{t.administration.users.noUsers}</div>
      ) : (
        <div className="admin-users-grid">
          {users.map((user) => (
            <AdminUserCard
              key={user.id}
              id={user.id}
              firstName={user.firstName}
              lastName={user.lastName}
              email={user.email}
              description={user.description}
              imageUrl={user.imageUrl}
              phoneNumber={user.phoneNumber}
              facebook={user.facebook}
              instagram={user.instagram}
              tiktok={user.tiktok}
              announcementCount={user.announcementCount ?? 0}
              announcementLimit={user.announcementLimit ?? 50}
              onClick={handleUserClick}
            />
          ))}
        </div>
      )}

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {userDetailModal.show && userDetailModal.user && (
        <AdminUserDetailModal
          user={userDetailModal.user}
          onClose={() => setUserDetailModal({ show: false, user: null })}
          onViewDetails={(userId) => {
            setUserDetailModal({ show: false, user: null });
            onNavigateToUserDetails(userId, getUserPublicName(userDetailModal.user!));
          }}
          onBlockUnblock={handleBlockUnblock}
          onIncreaseAnnouncementLimit={handleIncreaseAnnouncementLimit}
        />
      )}
    </div>
  );
};

export default AdminUsers;
